import _SignupViewRequestResetMutation from "../__generated__/SignupViewRequestResetMutation.graphql";
import _SignupViewLogInMutation from "../__generated__/SignupViewLogInMutation.graphql";
import _SignupViewSignUpMutation from "../__generated__/SignupViewSignUpMutation.graphql";
import { LogIn, TriangleAlert, UserPlus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { graphql, useMutation } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import type { SignupViewLogInMutation } from '../__generated__/SignupViewLogInMutation.graphql';
import type { SignupViewRequestResetMutation } from '../__generated__/SignupViewRequestResetMutation.graphql';
import type { SignupViewSignUpMutation } from '../__generated__/SignupViewSignUpMutation.graphql';
import { Field, LanguageField, LevelField } from '../components/Field.tsx';
import { CompactLanguagePicker } from '../components/LanguagePicker';
import { CastRow } from '../components/CastRow';
import { getLocale, langOf } from '../i18n/locale.ts';
import { t } from '../i18n/t';
import { authFailureCopy } from '../session/refusals.ts';
import { refusalFor } from '../profile/authReason.ts';
import { MAX_DISPLAY_NAME_CHARS, signupBody, validateCredentials, validateSignup, type FieldError, type FieldName, type SignupForm } from '../profile/profile';
const EMPTY: SignupForm = {
  email: '',
  password: '',
  displayName: '',
  languageChoice: '',
  otherLanguage: '',
  cefrLevel: 'B1'
};

/**
 * Create an account, or sign in to one.
 *
 * Relay mutations: credentials moved into the schema, so this speaks the same
 * protocol to the same endpoint as everything else. The session cookie is set
 * by the server on the mutation's own response — `HttpOnly`, so nothing here
 * ever sees it.
 */
const SignUp = (_SignupViewSignUpMutation.hash && _SignupViewSignUpMutation.hash !== "75fe0ea0b574a21e8fafae370e6d5356" && console.error("The definition of 'SignupViewSignUpMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SignupViewSignUpMutation);
const LogInM = (_SignupViewLogInMutation.hash && _SignupViewLogInMutation.hash !== "7a8f4af2dde1fb9e2a5943898063fb55" && console.error("The definition of 'SignupViewLogInMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SignupViewLogInMutation);
const RequestReset = (_SignupViewRequestResetMutation.hash && _SignupViewRequestResetMutation.hash !== "4c1d40a3820d80ee7d1d71ef7d1f55cd" && console.error("The definition of 'SignupViewRequestResetMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SignupViewRequestResetMutation);
type Mode = 'create' | 'signIn';

/**
 * The page around the form, shared by every state this screen has.
 *
 * Design §4: the bare `max-w-md` page served the two modes badly in opposite
 * directions — sign-in was two fields stranded in a full page of whitespace,
 * and create was five fields plus three buttons running off the bottom of a
 * phone with nothing to say the lower half was a different kind of question.
 * Shared rather than repeated so the "check your email" state does not jump
 * relative to the form it replaces.
 */
function Shell({
  children
}: {
  children: React.ReactNode;
}) {
  return <main className="first-run-ground flex min-h-dvh items-center justify-center p-4">
      <div className="flex w-full max-w-md flex-col gap-2">
        <CompactLanguagePicker />
        {/* First-impressions design §4: who you will talk to, before anything
            is asked. The register is unknown before sign-in, so the row
            shows both audiences. */}
        <CastRow />
        <p className="mb-2 text-center text-base-content/70">{t('signup-hook')}</p>
        <div className="card rise bg-base-100 shadow-sm">
          <div className="card-body">{children}</div>
        </div>
      </div>
    </main>;
}
export function SignupView({
  onSignedIn
}: {
  onSignedIn?: () => void;
}) {
  const navigate = useNavigate();
  // The route table renders this with no prop, so `done` always navigates in
  // practice. `onSignedIn` is an escape hatch for a caller that wants to
  // decide where next itself, same as `CharacterSelect`'s `onChosen` — not
  // exercised by any test today.
  const done = onSignedIn ?? (() => navigate('/', {
    replace: true
  }));
  const [mode, setMode] = useState<Mode>('create');
  const [form, setForm] = useState<SignupForm>(EMPTY);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [inFlight, setInFlight] = useState(false);
  const [sent, setSent] = useState(false);
  const [signUp] = useMutation<SignupViewSignUpMutation>(SignUp);
  const [logIn] = useMutation<SignupViewLogInMutation>(LogInM);
  const [requestReset] = useMutation<SignupViewRequestResetMutation>(RequestReset);

  // A message renders under its own field; one with no field is a problem
  // with the request rather than with anything the learner typed, and keeps
  // the alert. Design §4.
  const errorFor = (field: FieldName) => errors.find(e => e.field === field)?.message;
  const formErrors = errors.filter(e => e.field === null);

  /// A mutation the server refused outright or never answered, as distinct
  /// from one that answered `ok: false`. The message must not suggest the
  /// learner typed something wrong, and a rate limit says so (auth §12)
  /// rather than blaming the connection — `authFailureCopy`.
  const refused = useCallback((error: unknown) => {
    setErrors([{
      field: null,
      message: t(authFailureCopy(error))
    }]);
    setInFlight(false);
  }, []);
  const submit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    // Signing in needs only the credential; the profile fields are not on
    // screen and must not be validated against an empty form.
    const found = mode === 'create' ? validateSignup(form) : validateCredentials(form);
    setErrors(found);
    if (found.length > 0) return;
    setInFlight(true);
    if (mode === 'create') {
      signUp({
        variables: {
          input: signupBody(form, langOf(getLocale()))
        },
        onCompleted: ({
          signUp: result
        }) => {
          setInFlight(false);
          if (result.ok) {
            // Signup sets no cookie: it cannot sign in a taken address, and
            // an answer that differed between the two cases would say which
            // addresses are registered. The learner is told to check their
            // mail — the same thing the owner of a taken address is told,
            // which is the point.
            setSent(true);
            return;
          }
          const refusal = refusalFor(result.reason);
          setErrors([{
            field: refusal.field,
            message: t(refusal.message)
          }]);
        },
        onError: refused
      });
      return;
    }
    logIn({
      variables: {
        input: {
          email: form.email.trim(),
          password: form.password
        }
      },
      onCompleted: ({
        logIn: result
      }) => {
        setInFlight(false);
        if (result.ok) {
          // The cookie is on this mutation's own response; the caller
          // re-reads `me` rather than being handed an identity here, so
          // there is one source of it.
          done();
          return;
        }
        setErrors([{
          // No field: the server will not say which of the two was wrong,
          // and guessing one would tell a caller something the answer
          // deliberately does not.
          field: null,
          message: t(refusalFor(result.reason ?? 'CREDENTIALS_REFUSED').message)
        }]);
      },
      onError: refused
    });
  }, [done, form, logIn, mode, signUp, refused]);
  const askForALink = useCallback(() => {
    setInFlight(true);
    requestReset({
      variables: {
        email: form.email.trim()
      },
      onCompleted: () => {
        setInFlight(false);
        // Answered the same whether or not that address has an account, so
        // this screen says the same thing either way. "No such address" is
        // exactly the oracle the mutation refuses to be.
        setSent(true);
      },
      onError: refused
    });
  }, [form.email, requestReset, refused]);
  const creating = mode === 'create';
  if (sent) {
    return <Shell>
        <h1 className="text-2xl font-semibold">{t('signup-sent-title')}</h1>
        <p className="mt-1 text-sm text-base-content/60">{t('signup-sent-body')}</p>
        <p className="mt-4 text-sm text-base-content/60">{t('signup-sent-spam')}</p>
        <button className="btn btn-ghost mt-6" onClick={() => {
        setSent(false);
        setMode('signIn');
      }}>
          {t('signup-sent-back')}
        </button>
      </Shell>;
  }
  return <Shell>
      <h1 className="text-2xl font-semibold">{creating ? t('signup-create-title') : t('signup-sign-in-title')}</h1>
      <p className="mt-1 text-sm text-base-content/60">
        {creating ? t('signup-create-intro') : t('signup-sign-in-intro')}
      </p>

      <form className="mt-4 flex flex-col gap-1" onSubmit={submit}>
        <Field id="email" label={t('signup-email')} error={errorFor('email')} control={props => <input {...props} className={`${props.className} input`} type="email" autoComplete="email" value={form.email} onChange={e => setForm({
        ...form,
        email: e.target.value
      })} autoFocus />} />

        <Field id="password" label={t('signup-password')} error={errorFor('password')} control={props => <input {...props} className={`${props.className} input`} type="password" autoComplete={creating ? 'new-password' : 'current-password'} value={form.password} onChange={e => setForm({
        ...form,
        password: e.target.value
      })} />} />

        {creating && <>
            {/* The profile half, marked as a different kind of question. A
                learner reading a five-field form needs to know the lower half
                is short and about them, not more credential. */}
            <div className="divider divider-start mt-2 mb-0 text-xs opacity-60">{t('signup-about-you')}</div>

            <Field id="displayName" label={t('signup-display-name')} error={errorFor('displayName')} control={props => <input {...props} className={`${props.className} input`} value={form.displayName} onChange={e => setForm({
          ...form,
          displayName: e.target.value
        })} maxLength={MAX_DISPLAY_NAME_CHARS} />} />

            <LanguageField choice={form.languageChoice} other={form.otherLanguage} error={errorFor('nativeLanguage')} onChoice={v => setForm({
          ...form,
          languageChoice: v
        })} onOther={v => setForm({
          ...form,
          otherLanguage: v
        })} />

            {/* Adult: no character exists yet to say otherwise (pre-A1 §4). */}
            <LevelField value={form.cefrLevel} register="adult" onChange={v => setForm({
          ...form,
          cefrLevel: v
        })} />
          </>}

        {formErrors.length > 0 && <div className="alert alert-error mt-2" role="alert">
            <TriangleAlert aria-hidden className="size-5 shrink-0" />
            <ul className="flex flex-col gap-1 text-sm">
              {formErrors.map(e => <li key={e.message}>{e.message}</li>)}
            </ul>
          </div>}

        <button className="btn btn-primary mt-4" type="submit" disabled={inFlight}>
          {inFlight ? <span className="loading loading-spinner" aria-hidden /> : creating ? <UserPlus aria-hidden className="size-5" /> : <LogIn aria-hidden className="size-5" />}
          {/* "Start talking" promised that the next screen was a conversation;
              it is an email gate, and nothing is spoken until a link has been
              opened. The mic icon went with it — the first microphone a
              learner sees should be the first one that is used. The in-flight
              label was "Setting up…" in both modes, which told someone
              signing in to an account they already had that something was
              being set up. */}
          {inFlight ? t(creating ? 'signup-creating' : 'signup-signing-in') : t(creating ? 'signup-create' : 'signup-sign-in')}
        </button>

        <button type="button" className="btn btn-ghost btn-sm min-h-(--hit-min)" onClick={() => {
        setMode(creating ? 'signIn' : 'create');
        setErrors([]);
      }}>
          {t(creating ? 'signup-to-sign-in' : 'signup-to-create')}
        </button>

        {!creating && <button type="button" className="btn btn-ghost btn-sm min-h-(--hit-min)" disabled={inFlight || form.email.trim() === ''} onClick={askForALink}>
            {t('signup-forgot')}
          </button>}
      </form>
    </Shell>;
}